import { useEffect, useState } from 'react';

import { api, type CegLista, type CegSor, type EtteremReszletek } from '../api.ts';
import { useAlapadatok } from '../alapadatok-context.ts';
import { CegValaszto } from './CegValaszto.tsx';

const ADOSZAM_MINTA = /^\d{8}-\d-\d{2}$/;

interface UjAdatok {
  nev: string;
  irsz: string;
  cim: string;
  helyseg: string;
  netto_ar: string;
  fizetesi_mod: string;
  cegnev: string;
  adoszam: string;
  megjegyzes: string;
  csak_gyujtes: boolean;
}

/**
 * Új étterem felvétele.
 *
 * Csak a név kötelező — minden más utólag pótolható. Ez szándékos: az irodán
 * gyakran menet közben, telefon mellett vesznek fel új helyet, és ilyenkor
 * még nincs meg a cégadat. Ami hiányzik, azt a Törzsadatok szűrői úgyis
 * kigyűjtik később.
 */
export function UjEtterem({
  onKesz,
  onMegse,
  ceg: adottCeg = null,
}: {
  onKesz: (id: number) => void;
  onMegse: () => void;
  /** A Cégeknél „+ telephely": az új partner ennek a cégnek a telephelye lesz. */
  ceg?: CegSor | null;
}) {
  const [ceg, setCeg] = useState<CegSor | null>(adottCeg);
  /** A beírt adószámú, már meglévő cég — felkínáljuk, hogy ahhoz kerüljön. */
  const [adoszamCege, setAdoszamCege] = useState<CegSor | null>(null);
  const alapadatok = useAlapadatok();
  const [adatok, setAdatok] = useState<UjAdatok>({
    nev: '',
    irsz: '',
    cim: '',
    helyseg: '',
    netto_ar: '',
    fizetesi_mod: '',
    cegnev: '',
    adoszam: '',
    megjegyzes: '',
    csak_gyujtes: false,
  });
  const [hiba, setHiba] = useState<string | null>(null);
  const [ment, setMent] = useState(false);

  const allit =
    <K extends keyof UjAdatok>(k: K) =>
    (v: UjAdatok[K]) =>
      setAdatok((elozo) => ({ ...elozo, [k]: v }));

  const nev = adatok.nev.trim();
  const adoszam = adatok.adoszam.trim();
  const adoszamJo = !adoszam || ADOSZAM_MINTA.test(adoszam);
  // Ha a választott cégnek van adószáma, a partner azt kapja — nem írható át.
  const cegAdoszam = ceg?.adoszam ?? null;

  useEffect(() => {
    setAdoszamCege(null);
    if (ceg || !ADOSZAM_MINTA.test(adoszam)) return;
    const torzs = adoszam.slice(0, 8);
    let el = false;
    void api
      .get<CegLista>(`/api/ceg?q=${torzs}`)
      .then((l) => {
        if (!el) setAdoszamCege(l.sorok.find((c) => c.adoszam?.slice(0, 8) === torzs) ?? null);
      })
      .catch(() => undefined);
    return () => {
      el = true;
    };
  }, [adoszam, ceg]);

  async function felvesz() {
    setMent(true);
    setHiba(null);
    try {
      const uj = await api.post<EtteremReszletek>('/api/etterem', {
        ...adatok,
        adoszam: cegAdoszam ?? adatok.adoszam,
        ceg_id: ceg?.id ?? null,
      });
      onKesz(uj.id);
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A felvétel nem sikerült.');
    } finally {
      setMent(false);
    }
  }

  return (
    <div className="tdet">
      <h4>{adottCeg ? `Új telephely — ${adottCeg.nev}` : 'Új étterem'}</h4>
      <div className="g4">
        <Mezo cimke="Megnevezés *" ertek={adatok.nev} onValt={allit('nev')} />
        <Mezo cimke="Irányítószám" ertek={adatok.irsz} onValt={allit('irsz')} />
        <Mezo cimke="Cím" ertek={adatok.cim} onValt={allit('cim')} />
        <Mezo cimke="Helység" ertek={adatok.helyseg} onValt={allit('helyseg')} />

        <Mezo cimke="Nettó ár" ertek={adatok.netto_ar} onValt={allit('netto_ar')} />
        <div>
          <label>Fizetési mód</label>
          <select
            value={adatok.fizetesi_mod}
            onChange={(e) => allit('fizetesi_mod')(e.target.value)}
          >
            <option value="">Készpénz</option>
            {alapadatok.fizetesi_modok
              .filter((m) => m.kod !== 'KP')
              .map((m) => (
                <option key={m.kod} value={m.kod}>
                  {m.nev}
                </option>
              ))}
          </select>
        </div>
        <CegValaszto
          cegnev={adatok.cegnev}
          onCegnev={allit('cegnev')}
          ceg={ceg}
          onCeg={(c) => {
            setCeg(c);
            // A keresésre begépelt töredék nem cégnév: a partner a cég nevét kapja.
            if (c) allit('cegnev')('');
          }}
          rogzitett={!!adottCeg}
        />
        <div>
          <label>Adószám</label>
          {cegAdoszam ? (
            <div className="rdv" title="A cég adószáma — a telephely is ezt kapja">
              {cegAdoszam}
            </div>
          ) : (
            <input
              value={adoszam}
              placeholder="12345678-2-42"
              style={adoszamJo ? undefined : { borderColor: 'var(--bad)' }}
              title={adoszamJo ? undefined : 'Várt alak: 12345678-2-42'}
              onChange={(e) => allit('adoszam')(e.target.value)}
            />
          )}
          {!cegAdoszam && !adoszamJo && (
            <div className="mezohiba">Várt alak: 12345678-2-42</div>
          )}
        </div>

        <Mezo cimke="Megjegyzés" ertek={adatok.megjegyzes} onValt={allit('megjegyzes')} />
      </div>

      <div className="btns" style={{ gap: 18 }}>
        <label className="sw">
          <input
            type="checkbox"
            checked={adatok.csak_gyujtes}
            onChange={(e) => allit('csak_gyujtes')(e.target.checked)}
          />
          csak gyűjtés (nem adunk el olajat)
        </label>
      </div>

      {!ceg && adoszamCege && (
        <div className="uzenet flag">
          Ez az adószám a(z) <b>{adoszamCege.nev}</b> cégé ({adoszamCege.telephelyek}{' '}
          telephely) — a felvétellel ennek a telephelye lesz.{' '}
          <button className="btn sm" onClick={() => setCeg(adoszamCege)}>
            Kiválasztom a céget
          </button>
        </div>
      )}
      {!ceg && !adoszamCege && adatok.cegnev.trim() && (
        <div className="hint" style={{ margin: '6px 0' }}>
          Nincs kiválasztva meglévő cég: új cég jön létre „{adatok.cegnev.trim()}” néven.
        </div>
      )}

      {hiba && <div className="uzenet bad">{hiba}</div>}

      <div className="btns">
        <button
          className="btn pri sm"
          disabled={!nev || (!cegAdoszam && !adoszamJo) || ment}
          onClick={() => void felvesz()}
        >
          {ment ? 'Mentés…' : 'Felvétel'}
        </button>
        <button className="btn sm" onClick={onMegse}>
          Mégsem
        </button>
        <span className="hint">
          Csak a megnevezés kötelező. A többi utólag is pótolható — a szűrők
          kigyűjtik, hol maradt hiány.
        </span>
      </div>
    </div>
  );
}

function Mezo({
  cimke,
  ertek,
  onValt,
}: {
  cimke: string;
  ertek: string;
  onValt: (v: string) => void;
}) {
  return (
    <div>
      <label>{cimke}</label>
      <input value={ertek} onChange={(e) => onValt(e.target.value)} />
    </div>
  );
}
